import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

// biome-ignore lint/style/useImportType: keep top-level `import type` (not inline) so Vite fully elides it and does not bundle @langmail/database runtime into the client
import type { AccountStatus } from '@langmail/database'
import { Google, useAuth, useTRPC } from '@langmail/shared-ui'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { useToast } from '@/hooks/ui/use-toast'

import { DisconnectAccountDialog } from './disconnect-account-dialog'

interface AccountRowAccount {
  email_user: string
  gmail_writeback: boolean
  id: string
  oauth_error?: string | null
  status: AccountStatus
}

interface AccountRowProps {
  account: AccountRowAccount
  isPrimary: boolean
}

const STATUS_LABEL: Record<AccountStatus, string> = {
  connected: 'Connected',
  disconnected: 'Disconnected',
  error: 'Needs reconnect',
  connecting: 'Connecting…',
}

const STATUS_CLASS: Record<AccountStatus, string> = {
  connected: 'bg-emerald-100 text-emerald-800',
  disconnected: 'bg-muted text-muted-foreground',
  error: 'bg-amber-100 text-amber-800',
  connecting: 'bg-sky-100 text-sky-800',
}

export function AccountRow({ account, isPrimary }: AccountRowProps) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const { signInWithGoogle } = useAuth()
  const [showDisconnect, setShowDisconnect] = useState(false)
  const [isPrimaryReconnecting, setIsPrimaryReconnecting] = useState(false)

  const reconnect = useMutation({
    ...trpc.accounts.reconnect.mutationOptions(),
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({ queryKey: trpc.accounts.list.queryKey() })
      if (data.ok) {
        toast({ title: `Reconnected ${account.email_user}` })
      } else {
        toast({
          title: "Couldn't restore connection",
          description: 'Remove and add this account to reconnect.',
          variant: 'destructive',
        })
      }
    },
    onError: (error) => {
      toast({
        title: 'Reconnect failed',
        description: error.message ?? 'Please try again in a moment.',
        variant: 'destructive',
      })
    },
  })

  const writeback = useMutation({
    ...trpc.accounts.setGmailWriteback.mutationOptions(),
    // Write the confirmed value into the list cache the moment the mutation
    // resolves, so the switch holds its new position continuously — without
    // this, `isPending` flips false before the invalidation refetch lands and
    // the switch briefly snaps back to the stale value.
    onSuccess: (_data, { enabled }) => {
      queryClient.setQueryData(trpc.accounts.list.queryKey(), (old) =>
        old
          ? {
              ...old,
              accounts: old.accounts.map((a) => (a.id === account.id ? { ...a, gmail_writeback: enabled } : a)),
            }
          : old
      )
    },
    onError: (error) => {
      toast({
        title: "Couldn't update Gmail sync",
        description: error.message ?? 'Please try again in a moment.',
        variant: 'destructive',
      })
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: trpc.accounts.list.queryKey() }),
  })

  // Show the in-flight value while the toggle is saving so the switch feels
  // instant; `onSuccess` then pins the confirmed value in the cache, and a
  // failed toggle falls back to the server's (unchanged) value.
  const writebackEnabled =
    writeback.isPending && writeback.variables ? writeback.variables.enabled : account.gmail_writeback

  const handleReconnect = async () => {
    if (isPrimary) {
      setIsPrimaryReconnecting(true)
      const { error } = await signInWithGoogle({ isReconnect: true })
      if (error) {
        setIsPrimaryReconnecting(false)
        toast({ title: 'Reconnect failed', variant: 'destructive' })
      }
      return
    }
    reconnect.mutate({ accountId: account.id })
  }

  return (
    <>
      <div className="rounded-lg border border-border">
        <div className="flex h-11 items-center gap-3 px-3">
          <Google className="size-4 shrink-0" />
          <div className="flex-1 truncate">
            <div className="flex items-center gap-2">
              <span className="text-[15px] text-foreground">{account.email_user}</span>
              {isPrimary ? (
                <Badge className="text-[10px]" variant="secondary">
                  Primary
                </Badge>
              ) : null}
              <span className={`rounded-full px-2 py-0.5 font-medium text-[10px] ${STATUS_CLASS[account.status]}`}>
                {STATUS_LABEL[account.status]}
              </span>
            </div>
          </div>
          {account.status === 'error' && (
            <Button
              className="h-8 px-3"
              disabled={reconnect.isPending || isPrimaryReconnecting}
              onClick={handleReconnect}
              size="sm"
              variant="default"
            >
              {reconnect.isPending || isPrimaryReconnecting ? 'Reconnecting…' : 'Reconnect'}
            </Button>
          )}
          {!isPrimary && (
            <Button className="h-8 px-3" onClick={() => setShowDisconnect(true)} size="sm" variant="ghost">
              Disconnect
            </Button>
          )}
        </div>
        <div className="flex items-center justify-between gap-4 border-border border-t px-3 py-2">
          <label className="cursor-pointer text-[13px] text-muted-foreground" htmlFor={`writeback-${account.id}`}>
            Sync todo &amp; archive back to Gmail
          </label>
          <Switch
            checked={writebackEnabled}
            disabled={writeback.isPending}
            id={`writeback-${account.id}`}
            onCheckedChange={(enabled) => writeback.mutate({ accountId: account.id, enabled })}
          />
        </div>
      </div>
      {!isPrimary && (
        <DisconnectAccountDialog
          accountId={account.id}
          email={account.email_user}
          onOpenChange={setShowDisconnect}
          open={showDisconnect}
        />
      )}
    </>
  )
}
